// Step 3 of the import wizard: each word the file uses for one of our fixed
// lists (legal form, type of vendor, data they handle) is told once.
//
// # ONE FIELD AT A TIME (3.3, I3; the 7 Oct plan 4.4)
//
// Every field's table stood one under another, all open, and a warning was
// repeated under every row that needed it. Now the fields are a list on the
// left, each with what is left to answer (the list is also the progress), and
// one field's table is on the right: the values that need an answer first, then
// the suggestions to check, then the rest. What a field needs is said once,
// above its table. "Next field" moves on; Next on the footer leaves the step.
//
// A value with no answer is not a dead end: its vendors are rows to fix on the
// next step, as before, so Next stays open and says how many are left.

import { useEffect, useMemo, useRef, useState } from 'react'
import { ImportError } from '../../components/ImportUndo'
import { Chip } from '../../components/StatusChip'
import type { ImportValue, ImportValueSet } from '../../lib/api'
import { COLUMN_CHECK, COLUMN_MATCHED, plural } from '../../lib/importWords'
import { NEEDS_ANSWER, type ValueAnswers } from './shared'
import './values.css'

// A suggestion the importer was less sure of is shown to be checked.
const SURE = 0.9

function answerOf(s: ImportValueSet, answers: ValueAnswers, v: ImportValue): number {
  const a = answers[s.field]?.[v.value]
  return a === undefined ? -1 : s.options.findIndex((o) => o.code === a)
}

export type ValueState = 'open' | 'check' | 'done'

// The values a person has looked at and agreed with, as "field value" keys.
export type Agreed = ReadonlySet<string>
export const agreedKey = (field: string, value: string) => field + '\u0000' + value

// Where one value stands: no answer; the importer's own less-sure suggestion,
// still as it suggested and not yet agreed ("Looks right" or "Accept all"); or
// answered.
export function valueState(s: ImportValueSet, answers: ValueAnswers, v: ImportValue, agreed: Agreed = new Set()): ValueState {
  if (answerOf(s, answers, v) < 0) return 'open'
  const a = answers[s.field]?.[v.value]
  if (v.suggested && a === v.code && v.confidence < SURE && !agreed.has(agreedKey(s.field, v.value))) return 'check'
  return 'done'
}

export function openValues(sets: ImportValueSet[], answers: ValueAnswers): number {
  let n = 0
  for (const s of sets) for (const v of s.values) if (valueState(s, answers, v) === 'open') n++
  return n
}

// One field's progress, for its entry in the list.
export function fieldProgress(
  s: ImportValueSet,
  answers: ValueAnswers,
  agreed: Agreed = new Set(),
): { open: number; check: number; total: number } {
  let open = 0
  let check = 0
  for (const v of s.values) {
    const st = valueState(s, answers, v, agreed)
    if (st === 'open') open++
    else if (st === 'check') check++
  }
  return { open, check, total: s.values.length }
}

// The field's values in the order a person works them: no answer first, then
// suggestions to check, then the rest, each group by how many vendors use it.
export function orderedValues(s: ImportValueSet, answers: ValueAnswers, agreed: Agreed = new Set()): ImportValue[] {
  const rank: Record<ValueState, number> = { open: 0, check: 1, done: 2 }
  return [...s.values].sort(
    (a, b) => rank[valueState(s, answers, a, agreed)] - rank[valueState(s, answers, b, agreed)] || b.count - a.count,
  )
}

// What a field needs, said once above its table.
export function fieldNote(s: ImportValueSet, answers: ValueAnswers, agreed: Agreed = new Set()): string {
  const p = fieldProgress(s, answers, agreed)
  const values = s.values.length
  const ours = s.options.filter((o) => o.code !== '').length
  const head = 'Your file has ' + plural(values, 'value') + '; this register has ' + plural(ours, 'choice') + '.'
  if (p.open > 0) return head + ' ' + plural(p.open, 'needs', 'need') + ' an answer.'
  if (p.check > 0) return head + ' Check ' + plural(p.check, 'suggestion') + '.'
  return head + ' Every value has an answer.'
}

// A reason the importer gives on several rows of one field ("Your file has 3
// tiers and this register has 4") is the field's, and is said once above the
// table, not on every row.
export function sharedWhy(s: ImportValueSet): string {
  const seen = new Map<string, number>()
  for (const v of s.values) if (v.why) seen.set(v.why, (seen.get(v.why) ?? 0) + 1)
  for (const [why, n] of seen) if (n > 1) return why
  return ''
}

// The first field with a value to answer, else the first with a suggestion
// to check, else the first.
export function firstOpenField(sets: ImportValueSet[], answers: ValueAnswers): number {
  const open = sets.findIndex((s) => fieldProgress(s, answers).open > 0)
  if (open >= 0) return open
  const check = sets.findIndex((s) => fieldProgress(s, answers).check > 0)
  return check >= 0 ? check : 0
}

export function ValueStep({
  sets,
  answers,
  busy,
  error,
  onAnswer,
  onBack,
  onNext,
}: {
  sets: ImportValueSet[]
  answers: ValueAnswers
  busy: boolean
  error: unknown
  onAnswer: (field: string, value: string, code: string) => void
  onBack: () => void
  onNext: () => void
}) {
  const [at, setAt] = useState(() => firstOpenField(sets, answers))
  // Suggestions a person agreed with on this visit. The answers themselves are
  // the shell's and are kept; agreeing only clears the "check" mark.
  const [agreed, setAgreed] = useState<Agreed>(() => new Set())
  const agree = (field: string, values: string[]) =>
    setAgreed((cur) => new Set([...cur, ...values.map((v) => agreedKey(field, v))]))
  const open = openValues(sets, answers)
  const si = Math.min(at, sets.length - 1)
  const s = sets[si]
  // The order is set when a field is opened and kept while the person works in
  // it: a row answered does not move under the pointer or take the focus away.
  const order = useMemo(() => (s ? orderedValues(s, answers, agreed) : []), [si, sets]) // eslint-disable-line react-hooks/exhaustive-deps
  const heading = useRef<HTMLHeadingElement>(null)
  const moved = useRef(false)
  useEffect(() => {
    // After choosing another field, the focus goes to its heading (the button
    // pressed may be gone with the old field).
    if (moved.current) heading.current?.focus()
  }, [si])
  const go = (i: number) => {
    moved.current = true
    setAt(i)
  }
  const done = (x: ImportValueSet) => {
    const p = fieldProgress(x, answers, agreed)
    return p.open === 0 && p.check === 0
  }
  const toCheck = s ? s.values.filter((v) => valueState(s, answers, v, agreed) === 'check') : []

  return (
    <section className="panel imw-valstep" aria-labelledby="imw-vals-h">
      <div className="imw-valhead">
        <h2 id="imw-vals-h">Match values</h2>
        <p className="imw-colsum" aria-live="polite">
          {sets.filter(done).length} of {plural(sets.length, 'field')} done
          {open > 0 && <span> {plural(open, 'value needs', 'values need')} an answer</span>}
        </p>
      </div>
      <p className="imw-lead">Match your values to ours. Saved for next time.</p>

      <div className="imw-valgrid">
        <nav className="imw-fields" aria-label="Fields to match">
          <ul>
            {sets.map((x, i) => {
              const p = fieldProgress(x, answers, agreed)
              const word = p.open > 0 ? plural(p.open, 'to answer', 'to answer') : p.check > 0 ? plural(p.check, 'to check', 'to check') : 'Done'
              return (
                <li key={x.field + i}>
                  <button
                    type="button"
                    className={'imw-field' + (p.open > 0 ? ' imw-field-open' : p.check > 0 ? ' imw-field-check' : ' imw-field-done')}
                    aria-current={i === si ? 'step' : undefined}
                    aria-controls="imw-valset"
                    onClick={() => go(i)}
                  >
                    <span className="imw-field-name">{x.label}</span>
                    <span className="imw-field-state">{word}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>

        {s && (
          <div className="imw-valset" id="imw-valset">
            <h3 ref={heading} tabIndex={-1}>
              {s.label} <span className="muted">from your column {s.column}</span>
            </h3>
            <p className="imw-small imw-valnote">
              {fieldNote(s, answers, agreed)}
              {s.multi ? ' Each word in a cell is matched on its own.' : ''}
              {sharedWhy(s) ? ' ' + sharedWhy(s) + '.' : ''}
            </p>
            {toCheck.length > 0 && (
              <p>
                <button type="button" disabled={busy} onClick={() => agree(s.field, toCheck.map((v) => v.value))}>
                  Accept {plural(toCheck.length, 'suggestion')}
                </button>
              </p>
            )}
            <div className="table-wrap imw-valwrap" role="region" aria-label={'Values of ' + s.label} tabIndex={0}>
              <table className="grid imw-valtable">
                <caption className="vh">
                  {'The words in your column ' + s.column + ', how many vendors use each, and what each imports as'}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">In your file</th>
                    <th scope="col" className="num">
                      Vendors
                    </th>
                    <th scope="col">Imports as</th>
                    <th scope="col">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {order.map((v) => {
                    const oi = answerOf(s, answers, v)
                    const st = valueState(s, answers, v, agreed)
                    const sel = 'imw-v-' + si + '-' + s.values.indexOf(v)
                    return (
                      <tr key={sel} className={st === 'open' ? 'imw-open' : undefined}>
                        <th scope="row">{v.value === '' ? '(blank)' : v.value}</th>
                        <td className="num">{v.count.toLocaleString('en-IN')}</td>
                        <td>
                          <label htmlFor={sel} className="vh">
                            {'What ' + (v.value === '' ? 'a blank cell' : v.value) + ' imports as'}
                          </label>
                          <select
                            id={sel}
                            value={oi >= 0 ? String(oi) : ''}
                            disabled={busy}
                            onChange={(e) => {
                              const o = s.options[Number(e.target.value)]
                              if (o) onAnswer(s.field, v.value, o.code)
                            }}
                          >
                            {oi < 0 && <option value="">Choose what it imports as</option>}
                            {s.options.map((o, n) => (
                              <option key={n} value={String(n)}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {st === 'open' ? (
                            <Chip s={NEEDS_ANSWER} />
                          ) : st === 'check' ? (
                            <>
                              <Chip s={COLUMN_CHECK} />
                              <button type="button" className="linkish" disabled={busy} onClick={() => agree(s.field, [v.value])}>
                                Looks right<span className="vh"> for {v.value === '' ? 'a blank cell' : v.value}</span>
                              </button>
                            </>
                          ) : (
                            <Chip s={COLUMN_MATCHED} />
                          )}
                          {v.why && st !== 'done' && v.why !== sharedWhy(s) && <span className="imw-small">{v.why}</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {s.other > 0 && (
              <p className="imw-small">
                {plural(s.other, 'other value')} used by fewer vendors {s.other === 1 ? 'is' : 'are'} not listed. Vendors with
                them show on the next step if they need attention.
              </p>
            )}
            {si < sets.length - 1 && (
              <p>
                <button type="button" onClick={() => go(si + 1)}>
                  Next field: {sets[si + 1]?.label}
                </button>
              </p>
            )}
          </div>
        )}
      </div>

      <ImportError error={error} />
      <div className="imw-step-foot">
        <button type="button" onClick={onBack} disabled={busy}>
          Back
        </button>
        {open > 0 && (
          <p className="imw-why-not">
            {plural(open, 'value has', 'values have')} no answer: {open === 1 ? 'its vendors are' : 'their vendors are'} rows to fix on
            the next step.
          </p>
        )}
        <button type="button" className="primary imw-push" disabled={busy} onClick={onNext}>
          {busy ? 'Checking...' : 'Next'}
        </button>
      </div>
    </section>
  )
}
